import React, { useContext, useEffect, useMemo } from 'react';
import V5GadgetFormContext from '../../contexts/V5GadgetFormContext';
import MetricOptionsContext from '../../contexts/MetricOptionsContext';
import Dropdown from '../Inputs/Dropdown';
import styled from 'styled-components';
import Inputs from '../Inputs';
import Typography from '../../kingpin/atoms/Typography';
import { SortToggleContext } from './SortToggleContext';

const AbsoluteToggleContainer = styled.div`
  display: flex;
  padding: 8px;
  gap: 4px;
`;

const SortOptions = () => {
  const { series, dimensionA } = useContext(V5GadgetFormContext);
  const { defaultSort, setDefaultSort } = useContext(SortToggleContext);
  const { metricOptions } = useContext(MetricOptionsContext);

  // Reset the sort when the metric it points at is no longer one of the
  // series metrics (e.g. the user swapped the selected metric). Otherwise the
  // sort stays stale and its label falls back to showing the raw metric id.
  useEffect(() => {
    if (!defaultSort || defaultSort.metricId === undefined) {
      return;
    }
    const metricStillSelected = series.some(
      (s) => s.metricId === defaultSort.metricId,
    );
    if (!metricStillSelected) {
      setDefaultSort(undefined);
    }
  }, [series, defaultSort, setDefaultSort]);

  const selectedSortLabel = useMemo(() => {
    if (!defaultSort || !defaultSort.field) {
      return undefined;
    }
    const { field } = defaultSort;

    // Dimension sort — show the dimension field.
    if (dimensionA && field === dimensionA.field) {
      return dimensionA.field;
    }

    // Metric sort — resolve the name from the full metric options (via the
    // stored metricId, falling back to parsing the field) so we never display
    // the raw metric id.
    const metricId =
      defaultSort.metricId ??
      series.find(
        (s) => field === s.metricId || field.startsWith(`${s.metricId}-`),
      )?.metricId;
    const metricName = metricId
      ? metricOptions.find((m) => m.id === metricId)?.name
      : undefined;

    if (metricId && metricName) {
      if (field === `${metricId}-delta`) {
        return `${metricName} (delta)`;
      }
      if (field === `${metricId}-percentDelta`) {
        return `${metricName} (percentDelta)`;
      }
      return metricName;
    }

    return field;
  }, [defaultSort, dimensionA, metricOptions, series]);

  const dropdownOptions = useMemo(() => {
    const options: DropdownOption[] = [];
    series.forEach((s) => {
      const metricName = metricOptions.find((m) => m.id === s.metricId)?.name;

      if (!metricName) {
        return;
      }

      const metricOption = {
        label: metricName,
        onSelected: () =>
          setDefaultSort({
            ...defaultSort,
            field: s.metricId,
            metricId: s.metricId,
            isComparison: false,
            isAbsolute: false,
          }),
      };
      options.push(metricOption);

      // Add this option to the dropdown if there's a comparison for this metric, e.g. delta or percentDelta.
      let comparisonOption: DropdownOption;
      for (let cellTypeOption of ['delta', 'percentDelta']) {
        if (
          s.matrixCellType === 'none' ||
          (s.matrixCellType !== cellTypeOption && s.matrixCellType !== 'both')
        ) {
          continue;
        }
        const metricName = metricOptions.find((m) => m.id === s.metricId)?.name;

        const comparisonName = `${metricName || s.metricId} (${cellTypeOption})`;
        const comparisonValue = `${s.metricId}-${cellTypeOption}`;
        comparisonOption = {
          label: comparisonName,
          onSelected: () =>
            setDefaultSort({
              ...defaultSort,
              isComparison: true,
              metricId: s.metricId,
              field: comparisonValue,
            }),
        };

        options.push(comparisonOption);
      }
    });

    if (dimensionA) {
      options.push({
        label: dimensionA.field,
        onSelected: () =>
          setDefaultSort({
            ...defaultSort,
            field: dimensionA.field,
            isComparison: false,
            metricId: undefined,
            isAbsolute: false,
          }),
      });
    }

    return options;
  }, [defaultSort, dimensionA, metricOptions, series, setDefaultSort]);

  // Nothing to sort by until at least one metric or a dimension is configured.
  const hasSortableFields = series.length > 0 || !!dimensionA;

  return (
    <>
      <div style={{ display: 'flex' }}>
        <Dropdown
          selectedLabel={selectedSortLabel}
          options={dropdownOptions}
          isDisabled={!hasSortableFields}
        />
        <Dropdown
          selectedLabel={defaultSort?.direction}
          isDisabled={!hasSortableFields}
          options={[
            {
              label: 'asc',
              onSelected: () => {
                setDefaultSort({
                  ...defaultSort,
                  direction: 'asc',
                });
              },
            },
            {
              label: 'desc',
              onSelected: () =>
                setDefaultSort({
                  ...defaultSort,
                  direction: 'desc',
                }),
            },
          ]}
        />
      </div>

      {defaultSort?.isComparison && (
        <AbsoluteToggleContainer>
          <Inputs.Toggle
            value={defaultSort.isAbsolute || false}
            onChange={(e) =>
              setDefaultSort({
                ...defaultSort,
                isAbsolute: e,
              })
            }
          />
          <Typography.Body type="Label">Absolute</Typography.Body>
        </AbsoluteToggleContainer>
      )}
    </>
  );
};

export default SortOptions;
